import { Injectable } from '@angular/core';

import { BetSlipType, TaxationModel } from '@bpos';
import { BetType } from '@bpos/common';
import { SlipState } from '@bpos/v1/my-bets';
import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { MoneyService } from '@frontend/sports/common/core/feature/money';
import { BetslipListType } from '@frontend/sports/types/models/my-bets';
import { SyncUserSettingsService } from '@frontend/sports/user/feature';
import { Decimal } from 'decimal.js';

import { MyBetsBetSlip, MyBetsListContext, TokenType } from '../../my-bets-base/models/my-bets-viewmodels';

@Injectable({ providedIn: 'root' })
export class BetslipUtilService {
    constructor(
        private moneyService: MoneyService,
        private betslipConfig: BetslipConfig,
        private userSettings: SyncUserSettingsService,
    ) {}

    possibleWinningsValue(betslip: MyBetsBetSlip): string {
        if (betslip.isCachedOut) {
            return this.moneyService.transform(betslip.payoutAmount.value, true);
        }
        const token = betslip.usedRewardToken;

        switch (betslip.state) {
            case SlipState.Open:
                if (betslip.betType === BetSlipType.System && betslip.possibleWinnings.value === 0) {
                    return '-';
                }

                if (betslip.bets.some((bet) => bet.participantInfo?.isStartingPrice) || this.isAnyCastBetType(betslip)) {
                    return '-';
                }

                if (this.oddsAreBoosted(betslip)) {
                    return this.moneyService.transform(token!.additionalInformation!.boostedWinnings, true);
                }

                const openAmout = betslip.possibleWinnings.value;

                return this.moneyService.transform(openAmout, true);
            case SlipState.Won:
                if (this.oddsAreBoosted(betslip)) {
                    return this.moneyService.transform(token!.additionalInformation!.boostedWinnings, true);
                }

                // When settled won take the payout amount
                return this.moneyService.transform(betslip.payoutAmount.value, true);
            case SlipState.Lost:
                // Check for protected bet and recalculate payout amount (if applicable)
                if (betslip.isRefunded) {
                    betslip.payoutAmount.value = betslip.betProtectorInformation.fee + betslip.stake.value;
                }

                return betslip.payoutAmount.value === 0 ? '-' : this.moneyService.transform(betslip.payoutAmount.value, true);
            case SlipState.Canceled: {
                // when canceled take payout amount (stake but can be zero if bet was canceled with WITHHELD state) + betprotector fee if any.
                const canceledAmount = this.userSettings.isTurnoverTaxApplied
                    ? betslip.payoutAmount.value + this.getTaxAmount(betslip, TaxationModel.OnStake) + betslip.betProtectorInformation.fee
                    : betslip.payoutAmount.value + betslip.betProtectorInformation.fee;

                return this.moneyService.transform(canceledAmount, true);
            }
        }

        return '-';
    }

    boostedWinningsValue(betslip: MyBetsBetSlip): string {
        const rewardTokenInfo = betslip.usedRewardToken?.additionalInformation;
        const currency = rewardTokenInfo?.boostedWinningsCurrencyCode;

        if (!rewardTokenInfo?.winningsBoost || !rewardTokenInfo?.boostedWinnings || !currency) {
            return '';
        }

        return this.moneyService.transform(rewardTokenInfo.boostedWinnings, true, 2, false, '', currency);
    }

    isAccaBoostInfoIncluded(betslip: MyBetsBetSlip, myBetsListContext: MyBetsListContext): boolean {
        const isAccaBoost = betslip.usedRewardToken?.tokenType === TokenType.AccaBoostToken;
        if (!isAccaBoost) {
            return false;
        }

        const rewardTokenInfo = betslip.usedRewardToken?.additionalInformation;
        const currency = rewardTokenInfo?.boostedWinningsCurrencyCode;

        if (!rewardTokenInfo?.winningsBoost || !rewardTokenInfo?.boostedWinnings || !currency) {
            return false;
        }

        const open = myBetsListContext.listType === BetslipListType.Open;
        const live = myBetsListContext.listType === BetslipListType.Live;
        const won = myBetsListContext.listType === BetslipListType.Settled && betslip.state === SlipState.Won;
        const isEligibleBetType = open || live || won;

        return !betslip.isCachedOut && isEligibleBetType;
    }

    oddsAreBoosted(betslip: MyBetsBetSlip): boolean {
        if (!betslip.usedRewardToken) {
            return false;
        }

        const { tokenType, additionalInformation } = betslip.usedRewardToken;
        const hasRequiredData = tokenType === TokenType.OddsBoostToken && additionalInformation?.boostedNativeOdds;

        return !!hasRequiredData && betslip.state !== SlipState.Lost;
    }

    getTaxAmount(betslip: MyBetsBetSlip, taxationModel: TaxationModel): number {
        if (!!betslip.taxInformation.taxes && betslip.taxInformation.taxes.length > 0) {
            const data = betslip.taxInformation?.taxes.find((s) => s.model === taxationModel)?.amount.value;

            return this.getRounding(new Decimal(Number(data)));
        }

        return 0;
    }

    getRounding(val: Decimal, decimals: number = this.betslipConfig.roundValues ? 2 : 3) {
        return Number(val.toDecimalPlaces(decimals, this.betslipConfig.roundValues ? Decimal.ROUND_HALF_UP : Decimal.ROUND_HALF_FLOOR));
    }

    isAnyCastBetType(betslip: MyBetsBetSlip): boolean {
        return betslip.bets.some(
            (bet) =>
                bet.optionMarketInfo?.isStartingPrice ||
                (bet.optionMarketInfo?.betType &&
                    [BetType.CombinationForecast, BetType.CombinationTricast, BetType.Tricast, BetType.Forecast].includes(
                        bet.optionMarketInfo?.betType,
                    )),
        );
    }
}
